import { useEffect, useState } from "react";
import "./AdDocumentSign.scss";
import { useSelector } from "react-redux";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    TablePagination,
} from "@mui/material";
import { motion } from "framer-motion";
import { GridLoader, ScaleLoader } from "react-spinners";
import newRequest from "../../../utils/newRequest";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog";
import { CustomLoadingButton } from "../../../components/ui/CustomButton";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../toastService.js";
import app from "../../../utils/firebase";
import {
    getStorage,
    ref,
    uploadBytesResumable,
    getDownloadURL,
} from "firebase/storage";
import { MdClose, MdSave } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import UploadDialog from "../../../components/uploadDialog/UploadDialog.jsx";


const AdDocumentSign = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [documentSigns, setDocumentSigns] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedSign, setSelectedSign] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [img, setImg] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [signatureUrl, setSignatureUrl] = useState("");
    const [signName, setSignName] = useState("");
    const [signTitle, setSignTitle] = useState("");

    const fetchDocumentSigns = async () => {
        try {
            setIsProcessing(true);
            const res = await newRequest.get("/orgUnit/getSigns");
            setDocumentSigns(res.data);
            setSignatureUrl(res.data[0]?.sign_doc_url);
            setSelectedSign(res.data[0]);
            setSignName(res.data[0]?.sign_name || "");
            setSignTitle(res.data[0]?.sign_title || "");
        } catch (error) {
            console.error("Error fetching signatures:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const slicedSigns = filteredData.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    const handleRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 5));
        setCurrentPage(0);
    };


    const uploadToFirebase = async (file, fileFolder) => {
        if (!file) {
            const res = await newRequest.put(`/orgUnit/updSign/${selectedSign?._id}`, { sign_name: signName, sign_title: signTitle });
            const updatedData = [...documentSigns];
            const rowIndex = updatedData.findIndex(item => item?._id === selectedSign?._id);
            if (rowIndex !== -1) {
                updatedData[rowIndex]["sign_name"] = signName;
                updatedData[rowIndex]["sign_title"] = signTitle;
                setDocumentSigns(updatedData);
            }
            showToast(t('updated'), "success");
        } else {

            setIsUploadDialogOpen(true);
            const storage = getStorage(app);
            const fileName = new Date().getTime() + file.name;
            const storageRef = ref(storage, `qc_${currentUser.usr_org_code}/${fileFolder}/${fileName}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(Math.round(progress));
                },
                (error) => {
                    setIsUploadDialogOpen(false);
                    console.log(error);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const res = await newRequest.put(`/orgUnit/updSign/${selectedSign?._id}`, {
                        sign_doc_url: downloadURL,
                        sign_name: signName,
                        sign_title: signTitle
                    });
                    setSignatureUrl(downloadURL);
                    setImg(null);
                    setPreview(null);
                    const updatedData = [...documentSigns];
                    const rowIndex = updatedData.findIndex(item => item?._id === selectedSign?._id);
                    if (rowIndex !== -1) {
                        updatedData[rowIndex]["sign_doc_url"] = downloadURL;
                        updatedData[rowIndex]["sign_name"] = signName;
                        updatedData[rowIndex]["sign_title"] = signTitle;
                        setDocumentSigns(updatedData);
                    }
                    setIsUploadDialogOpen(false);
                    showToast(t('signatureSet', { param1: selectedSign?.sign_unit_code_xx, param2: selectedSign?.sign_doc_type_xx }), "success");
                }
            );
        }
    };

    useEffect(() => {
        const filteredResults = documentSigns?.filter(
            (item) =>
                item?.sign_unit_code?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                item?.sign_unit_code_xx?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                item?.sign_doc_type?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                item?.sign_doc_type_xx?.toLowerCase().includes(searchTerm?.toLowerCase())
        );
        setFilteredData(filteredResults);
    }, [documentSigns, searchTerm]);

    useEffect(() => {
        if (img) {
            const objectUrl = URL.createObjectURL(img);
            setPreview(objectUrl);
            return () => URL.revokeObjectURL(objectUrl);
        } else {
            setPreview(null);
        }
    }, [img]);


    useEffect(() => {
        try {
            setIsLoading(true)
            fetchDocumentSigns();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
            className="ad-document-sign">
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="ad-document-sign-wrapper">
                    <div className="ad-sign-content">
                        <div className="ad-sign-content-left">
                            <div className="search-input-wrapper" style={{ width: "100%" }}>
                                <FiSearch className="search-input-icon" />
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder={t('search')}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        try {
                                            setSearchTerm(e.target.value);
                                            if (e.target.value) {
                                                setCurrentPage(0);
                                            }
                                        } catch (error) {
                                            console.log(error);
                                        }
                                    }}
                                />
                                {searchTerm?.length > 0 && (
                                    <MdClose
                                        onClick={() => setSearchTerm("")}
                                        className="search-input-icon"
                                    />
                                )}
                            </div>
                            {filteredData?.length > 0 &&
                                <div className="table-content">
                                    <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                        <Table sx={{ width: "max-content", border: 'none' }}>
                                            <TableHead className="tablehead">
                                                <TableRow className="tablerow">
                                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                        #
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                        {t('unit').toUpperCase()}
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        {t('document').toUpperCase()}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody className="tablebody">
                                                {slicedSigns?.map((row, index) => (
                                                    <TableRow
                                                        key={row._id}
                                                        className={`${selectedSign?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                        onClick={() => {
                                                            try {
                                                                setImg(null);
                                                                setPreview(null);
                                                                setSignatureUrl(row?.sign_doc_url);
                                                                setSignName(row?.sign_name || "");
                                                                setSignTitle(row?.sign_title || "");
                                                                setSelectedSign(row);
                                                            } catch (error) {
                                                                console.log(error);
                                                            }
                                                        }}
                                                    >
                                                        <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                            <span>{currentPage * rowsPerPage + index + 1}</span>
                                                        </TableCell>
                                                        <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                                <span className="sub-title">{row?.sign_unit_code_xx}</span>
                                                                <span className="sub-title soft" style={{ fontSize: "10px" }}>{row?.sign_unit_code}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                                <span className="sub-title">{row?.sign_doc_type_xx}</span>
                                                                <span className="sub-title soft" style={{ fontSize: "10px" }}>{row?.sign_doc_type}</span>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        {filteredData?.length > rowsPerPage &&
                                            <TablePagination
                                                rowsPerPageOptions={[10]}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={handleRowsPerPage}
                                                component="div"
                                                count={filteredData?.length}
                                                rows={filteredData}
                                                page={currentPage}
                                                onPageChange={handlePageChange}
                                            />}
                                    </TableContainer>
                                </div>}
                        </div>
                        <div className="ad-sign-content-right">
                            <div className="navbar">
                                <div className="navbar-left">
                                    <span className="main-title">{`${selectedSign?.sign_unit_code_xx || ""} : ${selectedSign?.sign_doc_type || ""}`}</span>
                                </div>
                                <div className="navbar-right">
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#00D37F"
                                        tooltip="Save changes"
                                        endIcon={<MdSave />}
                                        onClick={async () => {
                                            // if (!img) {
                                            //     console.log("Please select an image first");
                                            //     // You might want to show a toast or alert here
                                            //     return;
                                            // }
                                            await uploadToFirebase(img, "signatures");
                                        }}
                                    // disabled={!img} // Disable button if no image is selected
                                    >
                                        {t('save')}
                                    </CustomLoadingButton>
                                </div>
                            </div>
                            <div className="name-title-wrapper">
                                <div className="name-view">
                                    <div className="input-item-with-label" style={{ width: "100%" }}>
                                        <input
                                            id="signName"
                                            name="signName"
                                            type="text"
                                            value={signName}
                                            placeholder={t('name')}
                                            onChange={(e) => setSignName(e.target.value)}
                                            style={{ width: "100%" }}
                                            className="form-input-item table"
                                        />
                                    </div>
                                </div>
                                <div className="title-view">
                                    <div className="input-item-with-label" style={{ width: "100%" }}>
                                        <input
                                            id="signTitle"
                                            name="signTitle"
                                            type="text"
                                            value={signTitle}
                                            placeholder={t('title')}
                                            onChange={(e) => setSignTitle(e.target.value)}
                                            style={{ width: "100%" }}
                                            className="form-input-item table"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="signature">
                                {preview ? (
                                    <img src={preview} alt="Selected" className="img" />
                                ) : signatureUrl ? (
                                    <img src={signatureUrl} alt="Signature" className="img" />
                                ) : (
                                    <img src="/img/noimage.png" alt="No image" className="img" />
                                )}

                                <input
                                    type="file"
                                    id="sign"
                                    style={{ display: "none" }}
                                    // accept="image/*"
                                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                                    onChange={(e) => {
                                        try {
                                            const file = e.target.files?.[0];
                                            // console.log("File chosen (raw):", file);
                                            if (file && file.type.startsWith("image/")) {
                                                setImg(file);
                                            } else {
                                                console.log("Invalid file type:", file?.type);
                                            }
                                            // Reset AFTER a tiny delay so React state has time to capture file
                                            setTimeout(() => {
                                                e.target.value = "";
                                            }, 0);
                                        } catch (error) {
                                            console.log(error);
                                        }
                                    }}
                                />
                                {["admin", "superadmin"].includes(currentUser.user_type_xx) && (
                                    <label htmlFor="sign" style={{ display: "flex", width: "250px", alignItems: "center", justifyContent: "center" }}>
                                        <div className="item" style={{ cursor: "pointer" }}>
                                            <h1 className="sub-title">{t(signatureUrl ? "updateSignature" : "selectSignature").toUpperCase()}</h1>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
            {/* progress dialog for file upload  */}
            <UploadDialog
                open={isUploadDialogOpen}
                handleClose={() => setIsUploadDialogOpen(false)}
                uploadProgress={uploadProgress}
            />
        </motion.div>
    )
};

export default AdDocumentSign;